import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import { getAuthToken } from '../../lib/authToken';

interface PortalMsg {
  id: string;
  clientId: string;
  caseId?: string | null;
  senderType: 'client' | 'staff';
  subject?: string | null;
  body: string;
  isRead: boolean;
  parentId?: string | null;
  threadRootId?: string | null;
  created_at?: string;
  createdAt?: string;
  client?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    entityLegalName?: string;
  };
}

const authHeaders = () => ({
  Authorization: `Bearer ${getAuthToken()}`,
  'Content-Type': 'application/json',
});

const clientLabel = (m: PortalMsg) => {
  const c = m.client;
  if (!c) return 'Client';
  if (c.entityLegalName) return c.entityLegalName;
  return [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || 'Client';
};

const PortalInboxPage: React.FC = () => {
  const [messages, setMessages] = useState<PortalMsg[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [thread, setThread] = useState<PortalMsg[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [replySubject, setReplySubject] = useState('');
  const [sending, setSending] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(true);

  const loadInbox = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = Boolean(opts?.silent);
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (unreadOnly) params.set('unreadOnly', 'true');
      const res = await fetch(`/api/portal-messages?${params}`, {
        headers: authHeaders(),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to load inbox');
      setMessages(json.data || []);
      setUnreadCount(json.meta?.unreadCount ?? 0);
      setError('');
    } catch (err: unknown) {
      if (!silent) {
        setError(toUserErrorMessage(err, 'Failed to load portal inbox'));
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [unreadOnly]);

  const refreshThread = useCallback(async (clientId: string) => {
    try {
      const res = await fetch(`/api/portal-messages/thread/${clientId}`, {
        headers: authHeaders(),
      });
      const json = await res.json();
      if (!res.ok) return;
      setThread(json.data?.messages || []);
    } catch {
      /* non-blocking */
    }
  }, []);

  useEffect(() => {
    void loadInbox();
  }, [loadInbox]);

  // Poll inbox (and open thread) so new client messages appear without a full page refresh
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      void loadInbox({ silent: true });
      if (selectedClientId) {
        void refreshThread(selectedClientId);
      }
    };
    const id = window.setInterval(tick, 12000);
    const onFocus = () => tick();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [loadInbox, refreshThread, selectedClientId]);

  const openThread = async (clientId: string) => {
    setSelectedClientId(clientId);
    setThreadLoading(true);
    try {
      const res = await fetch(`/api/portal-messages/thread/${clientId}`, {
        headers: authHeaders(),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to load thread'));
      setThread(json.data?.messages || []);

      // Mark unread client messages in this thread as read
      const unread = (json.data?.messages || []).filter(
        (m: PortalMsg) => m.senderType === 'client' && !m.isRead
      );
      await Promise.all(
        unread.map((m: PortalMsg) =>
          fetch(`/api/portal-messages/${m.id}/read`, {
            method: 'POST',
            headers: authHeaders(),
          })
        )
      );
      void loadInbox();
    } catch (err: unknown) {
      toast.error(toUserErrorMessage(err, 'Failed to load thread'));
    } finally {
      setThreadLoading(false);
    }
  };

  const sendReply = async () => {
    if (!selectedClientId || !replyBody.trim()) return;
    setSending(true);
    try {
      const parentId = thread.length ? thread[thread.length - 1].id : undefined;
      const res = await fetch(`/api/portal-messages/${selectedClientId}/reply`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          body: replyBody.trim(),
          subject: replySubject.trim() || undefined,
          parentId,
        }),
      });
      if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to send reply'));
      toast.success('Reply sent to client portal');
      setReplyOpen(false);
      setReplyBody('');
      setReplySubject('');
      await openThread(selectedClientId);
    } catch (err: unknown) {
      toast.error(toUserErrorMessage(err, 'Failed to send reply'));
    } finally {
      setSending(false);
    }
  };

  const groupedClients = useMemo(() => {
    const map = new Map<string, { clientId: string; label: string; latest: PortalMsg; unread: number }>();
    for (const m of messages) {
      const existing = map.get(m.clientId);
      const created = m.created_at || m.createdAt || '';
      if (!existing) {
        map.set(m.clientId, {
          clientId: m.clientId,
          label: clientLabel(m),
          latest: m,
          unread: m.senderType === 'client' && !m.isRead ? 1 : 0,
        });
      } else {
        const existingCreated = existing.latest.created_at || existing.latest.createdAt || '';
        if (created > existingCreated) existing.latest = m;
        if (m.senderType === 'client' && !m.isRead) existing.unread += 1;
      }
    }
    return Array.from(map.values());
  }, [messages]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h4">Portal Inbox</Typography>
          <Typography variant="body2" color="text.secondary">
            Client portal messages — {unreadCount} unread
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant={unreadOnly ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setUnreadOnly(true)}
          >
            Unread
          </Button>
          <Button
            variant={!unreadOnly ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setUnreadOnly(false)}
          >
            All recent
          </Button>
          <Button size="small" onClick={() => void loadInbox()}>
            Refresh
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '340px 1fr' }, gap: 2 }}>
          <Paper variant="outlined" sx={{ maxHeight: '70vh', overflow: 'auto' }}>
            {groupedClients.length === 0 ? (
              <Box sx={{ p: 3 }}>
                <Typography color="text.secondary">
                  {unreadOnly ? 'No unread client messages.' : 'No portal messages yet.'}
                </Typography>
              </Box>
            ) : (
              <List dense disablePadding>
                {groupedClients.map((g) => (
                  <ListItem key={g.clientId} disablePadding divider>
                    <ListItemButton
                      selected={selectedClientId === g.clientId}
                      onClick={() => void openThread(g.clientId)}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2">{g.label}</Typography>
                            {g.unread > 0 && (
                              <Chip size="small" color="error" label={g.unread} />
                            )}
                          </Box>
                        }
                        secondary={
                          <>
                            {(g.latest.subject || g.latest.body || '').slice(0, 80)}
                            <br />
                            {format(
                              new Date(g.latest.created_at || g.latest.createdAt || Date.now()),
                              'MMM d, yyyy h:mm a'
                            )}
                          </>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, minHeight: 360, maxHeight: '70vh', overflow: 'auto' }}>
            {!selectedClientId ? (
              <Typography color="text.secondary">Select a client conversation.</Typography>
            ) : threadLoading ? (
              <CircularProgress size={28} />
            ) : (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">Conversation</Typography>
                  <Button variant="contained" size="small" onClick={() => setReplyOpen(true)}>
                    Reply
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                {thread.length === 0 ? (
                  <Typography color="text.secondary">No messages in this thread.</Typography>
                ) : (
                  thread.map((m) => (
                    <Box
                      key={m.id}
                      sx={{
                        mb: 2,
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: m.senderType === 'staff' ? 'action.hover' : 'background.default',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {m.senderType === 'staff' ? 'Staff' : 'Client'}
                        {' · '}
                        {format(new Date(m.created_at || m.createdAt || Date.now()), 'MMM d, yyyy h:mm a')}
                        {m.senderType === 'client' && !m.isRead ? ' · unread' : ''}
                      </Typography>
                      {m.subject ? (
                        <Typography variant="subtitle2">{m.subject}</Typography>
                      ) : null}
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {m.body}
                      </Typography>
                    </Box>
                  ))
                )}
              </>
            )}
          </Paper>
        </Box>
      )}

      <Dialog open={replyOpen} onClose={() => setReplyOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Reply to client</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Subject (optional)"
            value={replySubject}
            onChange={(e) => setReplySubject(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            required
            multiline
            minRows={4}
            label="Message"
            value={replyBody}
            onChange={(e) => setReplyBody(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplyOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={sending || !replyBody.trim()}
            onClick={() => void sendReply()}
          >
            {sending ? 'Sending…' : 'Send reply'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PortalInboxPage;
